import React, { type ReactNode, useMemo } from 'react';

import { graphql, useFragment } from 'react-relay';

import { getProductAndIDFromARI } from '@atlassian/search-client/ari';
import { type SearchResultType as SearchClientResultType } from '@atlassian/search-client/common/types';
import { getJiraUrlWithAnalyticsQueryParams } from '@atlassian/search-common/analytics-query-params';
import { highlightMatchedText } from '@atlassian/search-common/highlight-matched-text';

import { ProductKeys } from '../../../../../../common/constants/products';
import type { SearchResultProps } from '../../../../../../common/types';
import { DescriptionBody } from '../../../../../../common/ui/description-body';
import { LastModified } from '../../../../../../common/ui/last-modified';
import { getJiraEntityIcon } from '../../../../../../common/ui/product-entity-icon';
import { SearchResult } from '../../../../../../common/ui/search-result';
import { SearchResultType } from '../../../../../../common/ui/search-result-type';
import type { AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { isDummyBoardUrl } from '../../../../../../common/utils/is-dummy-board-url';
import { enrichExcerpt, processExcerpt } from '../../../../../../common/utils/search-result-utils';
import { getSummarizedAdfContent } from '../../../../../../common/utils/summarized-adf-content';
import { useLogException } from '../../../../../../controllers/error-boundary/utils';
import { useFireContextMenuButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-button-clicked';
import { useFireContextMenuCopyButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-copy-button-clicked';
import { useFireCopyLinkButtonClicked } from '../../../../../../controllers/events/use-fire-copy-link-button-clicked';
import { useFireResultClicked } from '../../../../../../controllers/events/use-fire-result-clicked';
import { useFireResultCtxMenuClicked } from '../../../../../../controllers/events/use-fire-result-context-menu-clicked';
import { useFireSummarizeInChatButtonClicked } from '../../../../../../controllers/events/use-fire-summarize-in-chat-button-clicked';

import type { searchJiraBoard_AtlassianSearchPage$key } from './__generated__/searchJiraBoard_AtlassianSearchPage.graphql';

export const SearchJiraBoard = ({
	node,
	position,
	cloudId,
	searchQuery,
	searchSessionId,
	selectedProduct,
	primaryProduct,
	primarySubProduct,
	appMode,
	mainContentWidth,
}: SearchResultProps<searchJiraBoard_AtlassianSearchPage$key>): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchJiraBoard_AtlassianSearchPage on SearchResultJiraBoard {
				__typename
				id
				title
				url
				type
				description
				lastModifiedDate
				iconUrl
				board {
					iconUrl
					boardFrontendUrl
				}
			}
		`,
		node,
	);

	const logException = useLogException();
	const metadata = useMemo(() => {
		const [product, , , contentId] = getProductAndIDFromARI(data.id);
		return {
			id: data.id,
			containerId: null,
			spaceId: null,
			contentType: data.type,
			entityType: data.type,
			contentId,
			product,
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [data.id, data.type]);
	const onResultClicked = useFireResultClicked({
		metadata,
		position,
	});
	const onResultCtxMenuClicked = useFireResultCtxMenuClicked({
		metadata,
		position,
	});

	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');
	const onSummarizeInChatButtonClicked = useFireSummarizeInChatButtonClicked({
		location: 'searchResult',
		metadata,
		position,
	});
	const onContextMenuCopyButtonClicked = useFireContextMenuCopyButtonClicked();
	const onContextMenuButtonClicked = useFireContextMenuButtonClicked();

	const url = useMemo(() => {
		const notDummyBoardUrl = isDummyBoardUrl(data.url) ? undefined : data.url;
		const boardUrl = data.board?.boardFrontendUrl || notDummyBoardUrl;

		return boardUrl
			? getJiraUrlWithAnalyticsQueryParams({
					url: boardUrl,
					searchSessionId,
					resultId: data.id,
				})
			: '';
	}, [data.url, data.id, data.board, searchSessionId]);

	const enrichedTitle = useMemo(() => {
		const { highlightedTitle } = highlightMatchedText(data.title, searchQuery.split(' '));

		return !highlightedTitle ? data.title : enrichExcerpt(highlightedTitle);
	}, [data.title, searchQuery]);

	const enrichedDescription = useMemo(() => {
		const reducedDescription = getSummarizedAdfContent(data.description, searchQuery);
		const { highlightedTitle } = highlightMatchedText(reducedDescription, searchQuery.split(' '));
		if (!highlightedTitle) {
			return reducedDescription;
		}
		return processExcerpt(highlightedTitle);
	}, [data.description, searchQuery]);

	const metadataParts: [string, ReactNode][] = useMemo(() => {
		const parts = [
			['type', <SearchResultType type={data.type as SearchClientResultType} />],
			[
				'lastModified',
				data.lastModifiedDate ? <LastModified lastModified={data.lastModifiedDate} /> : null,
			],
		];
		return parts.filter(Boolean) as [string, ReactNode][];
	}, [data.type, data.lastModifiedDate]);

	// Don't render anything if there's no URL
	if (!url) {
		logException(new Error(String(`Jira Board search result has no URL. Result ID: ${data.id}`)));
		return <></>;
	}

	const iconUrl = data.board?.iconUrl || data.iconUrl;

	const icon = getJiraEntityIcon({
		iconUrl,
		type: data.type,
		isIssue: false,
		size: 'medium',
	});
	return (
		<SearchResult
			{...data}
			product={ProductKeys.Jira}
			productSelected={!!selectedProduct}
			primaryProduct={primaryProduct}
			primarySubProduct={primarySubProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultCtxMenuClicked}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			url={url}
			icon={icon}
			title={enrichedTitle}
			headerMetadataParts={metadataParts}
			description={<DescriptionBody description={enrichedDescription} />}
			cloudId={cloudId}
			position={position}
			partial={data}
			appMode={appMode}
			mainContentWidth={mainContentWidth}
		/>
	);
};

export const isSearchJiraBoard = (node?: any): node is searchJiraBoard_AtlassianSearchPage$key => {
	return node?.__typename === 'SearchResultJiraBoard';
};
